import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Calendar,
  ExternalLink,
  FileIcon,
  Fingerprint,
  Info,
  RotateCcw,
  ScanSearch,
  Trash2,
} from 'lucide-react';
import { cn } from '@/client/lib/utils';
import { getAsset } from '@/client/utils/assets';
import { assetState } from './AssetCard';
import AssetImage from './AssetImage';
import AssetSignalsPanel from './AssetSignalsPanel';
import AssetModerationPanel from './AssetModerationPanel';
import AssetProvenancePanel from './AssetProvenancePanel';
import AssetManagePanel from './AssetManagePanel';

interface AssetDetailsDrawerProps {
  asset: Asset | null;
  isOpen: boolean;
  onClose: () => void;
  onDelete: (asset: Asset) => void;
  onRestore: (asset: Asset) => void;
  onFindSimilar: (asset: Asset) => void;
  pending?: boolean;
}

const Field: React.FC<{ label: string; children: React.ReactNode; mono?: boolean }> = ({
  label,
  children,
  mono,
}) => (
  <div className="space-y-1">
    <span className="text-xs text-muted-foreground">{label}</span>
    <div className={cn('text-sm font-medium break-words', mono && 'font-mono text-xs')}>
      {children}
    </div>
  </div>
);

// Boolean asset attributes worth surfacing for moderation — only the truthy
// ones are shown, so the panel stays honest about what's actually set.
const ATTRIBUTES: { key: keyof Asset; label: string }[] = [
  { key: 'featured', label: 'Featured' },
  { key: 'cover', label: 'Cover' },
  { key: 'default', label: 'Default' },
  { key: 'private', label: 'Private' },
  { key: 'protected', label: 'Protected' },
  { key: 'corrupt', label: 'Corrupt' },
  { key: 'advert', label: 'Advert' },
  { key: 'thumbnail', label: 'Thumbnail' },
  { key: 'logo', label: 'Logo' },
  { key: 'stock', label: 'Stock' },
  { key: 'is_360', label: '360°' },
  { key: 'is_virtual_tour', label: 'Virtual tour' },
  { key: 'floor_plan', label: 'Floor plan' },
  { key: 'site_plan', label: 'Site plan' },
];

export const AssetDetailsDrawer: React.FC<AssetDetailsDrawerProps> = ({
  asset,
  isOpen,
  onClose,
  onDelete,
  onRestore,
  onFindSimilar,
  pending,
}) => {
  if (!asset) return null;

  const isImage = asset.kind ? asset.kind === 'image' : (asset.mime_type?.startsWith('image/') ?? false);
  const imageUrl = asset.image_urls ? asset.image_urls[0] : getAsset(asset);
  // Library rows carry display_url (null for private media, whose object is ACL'd).
  const originalUrl = asset.image_urls ? asset.display_url : asset.public_url || imageUrl;
  const deleted = asset.deleted === 1;
  const state = assetState(asset);
  const phash = (asset as any).phash as string | undefined;
  const activeAttrs = ATTRIBUTES.filter((a) => (asset[a.key] as number) === 1);

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex h-full w-[400px] flex-col border-l bg-background p-0 shadow-2xl sm:w-[540px]">
        <div className="border-b p-6">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              Asset #{asset.asid}
              <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', state.cls)}>
                {state.label}
              </span>
            </SheetTitle>
            <SheetDescription>Review and moderate this media asset.</SheetDescription>
          </SheetHeader>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="space-y-6 p-6">
            {/* Preview */}
            <div className="overflow-hidden rounded-xl border bg-muted/30 shadow-sm">
              {isImage && imageUrl ? (
                <div className="flex aspect-video items-center justify-center bg-black/5">
                  <AssetImage key={asset.asid} asset={asset} className="max-h-[300px] w-full object-contain" />
                </div>
              ) : (
                <div className="flex aspect-video flex-col items-center justify-center bg-muted">
                  <FileIcon className="mb-2 h-16 w-16 text-muted-foreground/50" />
                  <p className="text-sm text-muted-foreground">{asset.mime_type || 'No preview'}</p>
                </div>
              )}
              <div className="flex items-center justify-between border-t bg-card p-3">
                <div className="truncate text-sm font-medium" title={asset.name}>
                  {asset.name || 'Untitled'}
                </div>
                {originalUrl && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(originalUrl, '_blank', 'noopener')}
                  >
                    <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Open
                  </Button>
                )}
              </div>
            </div>

            {/* Metadata */}
            <div>
              <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold">
                <Info className="h-4 w-4" /> Details
              </h4>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                <Field label="File type">
                  <span className="inline-flex rounded-full bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground">
                    {asset.mime_type || 'unknown'}
                  </span>
                </Field>
                <Field label="Added">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="h-3 w-3 text-muted-foreground" />
                    {asset.date_added ? new Date(asset.date_added).toLocaleDateString() : '—'}
                  </span>
                </Field>
                <Field label="Listing (eid)">{asset.eid ? `#${asset.eid}` : '—'}</Field>
                <Field label="Owner (uid)">{asset.uid ? `#${asset.uid}` : '—'}</Field>
                <Field label="Org (oid)">{asset.oid ? `#${asset.oid}` : '—'}</Field>
                <Field label="Modified">
                  {asset.date_modified ? new Date(asset.date_modified).toLocaleDateString() : '—'}
                </Field>
                <div className="col-span-2">
                  <Field label="UUID" mono>
                    {asset.uuid || '—'}
                  </Field>
                </div>
                {(asset.path_root || asset.path) && (
                  <div className="col-span-2">
                    <Field label="Storage path" mono>
                      {`${asset.path_root ?? ''}/${asset.path ?? ''}`}
                    </Field>
                  </div>
                )}
                {phash && (
                  <div className="col-span-2">
                    <Field label="Perceptual hash" mono>
                      <span className="flex items-center gap-1.5">
                        <Fingerprint className="h-3 w-3 text-muted-foreground" />
                        {phash}
                      </span>
                    </Field>
                  </div>
                )}
              </div>
            </div>

            {activeAttrs.length > 0 && (
              <>
                <Separator />
                <div>
                  <h4 className="mb-3 text-sm font-semibold">Attributes</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {activeAttrs.map((a) => (
                      <span
                        key={a.key as string}
                        className="rounded-full border bg-muted/40 px-2.5 py-0.5 text-xs font-medium"
                      >
                        {a.label}
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}

            <Separator />
            <AssetManagePanel key={`m-${asset.asid}`} asset={asset} />

            <Separator />
            <AssetModerationPanel asset={asset} />

            <Separator />
            <AssetSignalsPanel key={asset.asid} asset={asset} />

            <Separator />
            <AssetProvenancePanel key={`p-${asset.asid}`} asset={asset} />
          </div>
        </div>

        {/* Moderation actions */}
        <div className="border-t bg-muted/10 p-6">
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => onFindSimilar(asset)}
              disabled={!originalUrl}
            >
              <ScanSearch className="mr-2 h-4 w-4" /> Find similar
            </Button>
            {deleted ? (
              <Button className="flex-1" onClick={() => onRestore(asset)} disabled={pending}>
                <RotateCcw className="mr-2 h-4 w-4" /> Restore
              </Button>
            ) : (
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() => onDelete(asset)}
                disabled={pending}
              >
                <Trash2 className="mr-2 h-4 w-4" /> Delete
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
